<template>
	<!-- Campana del header: contador de no leídas y panel con la lista.
	     La conexión en vivo, el sonido y los avisos del sistema están en
	     Utils/notificaciones.js; esto solo muestra el store `notificaciones`. -->
	<div class="nt" @keydown.esc.stop="cerrar(true)">
		<button
			ref="btn"
			type="button"
			:class="['nt-btn', { 'has-new': noLeidas > 0, 'is-ring': sonando }]"
			aria-haspopup="dialog"
			:aria-expanded="String(abierto)"
			aria-controls="nt-pop"
			:aria-label="noLeidas > 0 ? $t('notificaciones.campana_n', { n: noLeidas }) : $t('notificaciones.campana')"
			@click.stop="alternar"
		>
			<span class="nt-bell" v-html="ICON.bell"></span>
			<span v-if="noLeidas > 0" class="nt-count" aria-hidden="true">{{ noLeidas > 99 ? '99+' : noLeidas }}</span>
		</button>

		<div
			v-show="abierto"
			id="nt-pop"
			ref="pop"
			class="nt-pop"
			role="dialog"
			aria-labelledby="nt-title"
			@click.stop
		>
			<header class="nt-head">
				<h2 id="nt-title" class="nt-title">{{ $t('notificaciones.titulo') }}</h2>
				<button v-if="noLeidas > 0" type="button" class="nt-link" @click="leerTodas">
					<span v-html="ICON.check"></span>{{ $t('notificaciones.leer_todas') }}
				</button>
			</header>

			<div class="nt-tabs" role="group" :aria-label="$t('notificaciones.filtro')">
				<button type="button" :class="['nt-tab', { 'is-on': filtro === 'todas' }]" :aria-pressed="String(filtro === 'todas')" @click="filtro = 'todas'">{{ $t('notificaciones.todas') }}</button>
				<button type="button" :class="['nt-tab', { 'is-on': filtro === 'sin_leer' }]" :aria-pressed="String(filtro === 'sin_leer')" @click="filtro = 'sin_leer'">
					{{ $t('notificaciones.sin_leer') }}<span v-if="noLeidas > 0" class="nt-tab-n">{{ noLeidas }}</span>
				</button>
			</div>

			<div class="nt-body">
				<ul v-if="!cargado && cargando" class="nt-list" aria-hidden="true">
					<li v-for="i in 3" :key="i" class="nt-skel"><span></span><span></span></li>
				</ul>

				<div v-else-if="error && !items.length" class="nt-empty">
					<p class="nt-empty-title">{{ $t('notificaciones.error') }}</p>
					<button type="button" class="nt-btn-sec" @click="cargar()">{{ $t('notificaciones.reintentar') }}</button>
				</div>

				<div v-else-if="!lista.length" class="nt-empty">
					<span class="nt-empty-ico" v-html="ICON.bell"></span>
					<p class="nt-empty-title">{{ filtro === 'sin_leer' ? $t('notificaciones.vacio_sin_leer') : $t('notificaciones.vacio') }}</p>
					<p class="nt-empty-text">{{ $t('notificaciones.vacio_texto') }}</p>
				</div>

				<ul v-else class="nt-list" :aria-label="$t('notificaciones.titulo')">
					<li v-for="n in lista" :key="n.id">
						<button type="button" :class="['nt-item', `nt-item--${icono(n)}`, { 'is-unread': !n.leida }]" @click="abrirNotificacion(n)">
							<span class="nt-ico" v-html="ICON[icono(n)] || ICON.msg"></span>
							<span class="nt-txt">
								<span class="nt-item-title">{{ texto(n).titulo }}</span>
								<span v-if="texto(n).cuerpo" class="nt-item-body">{{ texto(n).cuerpo }}</span>
								<span class="nt-item-meta">
									<time :datetime="n.fecha">{{ hace(n.fecha) }}</time>
									<template v-if="texto(n).meta"> · {{ texto(n).meta }}</template>
								</span>
							</span>
							<span v-if="!n.leida" class="nt-dot"><span class="nt-sr">{{ $t('notificaciones.sin_leer') }}</span></span>
						</button>
					</li>
				</ul>

				<button v-if="hayMas && filtro === 'todas' && items.length" type="button" class="nt-more" :disabled="cargando" @click="cargar(true)">
					{{ cargando ? $t('notificaciones.cargando') : $t('notificaciones.ver_mas') }}
				</button>
			</div>

			<footer class="nt-foot">
				<div class="nt-pref">
					<span class="nt-pref-ico" v-html="silenciado ? ICON.mute : ICON.sound"></span>
					<span class="nt-pref-txt">
						<span id="nt-pref-sonido" class="nt-pref-title">{{ $t('notificaciones.sonido') }}</span>
					</span>
					<button type="button" role="switch" class="nt-switch" :aria-checked="String(!silenciado)" aria-labelledby="nt-pref-sonido" @click="alternarSonido"><span></span></button>
				</div>
				<div v-if="permiso !== 'unsupported'" class="nt-pref">
					<span class="nt-pref-ico" v-html="ICON.screen"></span>
					<span class="nt-pref-txt">
						<span id="nt-pref-escritorio" class="nt-pref-title">{{ $t('notificaciones.escritorio') }}</span>
						<span class="nt-pref-hint">{{ permiso === 'denied' ? $t('notificaciones.escritorio_bloqueado') : $t('notificaciones.escritorio_texto') }}</span>
					</span>
					<button type="button" role="switch" class="nt-switch" :disabled="permiso === 'denied'" :aria-checked="String(escritorioOn)" aria-labelledby="nt-pref-escritorio" @click="alternarEscritorio"><span></span></button>
				</div>
			</footer>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import ICON from './iconos';
import { textoNotificacion, haceCuanto, familia } from './textos';
import { abrir, sonar, permisoEscritorio, pedirPermisoEscritorio } from '@/Utils/notificaciones';

export default {
	name: 'NotificacionesCampana',
	data() {
		return {
			ICON,
			abierto: false,
			filtro: 'todas',
			permiso: permisoEscritorio(),
			sonando: false,
			// Para que "hace 5 min" se actualice con el panel abierto.
			ahora: Date.now(),
			reloj: null
		};
	},
	computed: {
		...mapState('notificaciones', ['items', 'noLeidas', 'cargando', 'cargado', 'error', 'hayMas', 'silenciado', 'escritorio']),
		lista() {
			return this.filtro === 'sin_leer' ? this.items.filter(n => !n.leida) : this.items;
		},
		escritorioOn() {
			return this.permiso === 'granted' && this.escritorio;
		}
	},
	watch: {
		// Con el panel abierto los carteles se esconden (si no, tapan el panel).
		abierto(v) {
			document.documentElement.classList.toggle('nt-panel-abierto', v);
		},
		// La campana se mueve un instante cuando llega algo nuevo.
		noLeidas(n, antes) {
			if (n > antes) {
				this.sonando = false;
				this.$nextTick(() => { this.sonando = true; });
				clearTimeout(this.timerRing);
				this.timerRing = setTimeout(() => { this.sonando = false; }, 1000);
			}
		},
		$route() {
			this.cerrar();
		}
	},
	methods: {
		texto(n) {
			// eslint-disable-next-line no-unused-expressions
			this.$i18n.locale;
			return textoNotificacion(n, this.$i18n);
		},
		icono(n) {
			return familia(n);
		},
		hace(f) {
			// eslint-disable-next-line no-unused-expressions
			this.ahora;
			return haceCuanto(f, this.$i18n.locale);
		},
		alternar() {
			if (this.abierto) return this.cerrar(true);
			this.$emit('abrir');
			this.abierto = true;
			this.permiso = permisoEscritorio();
			this.ahora = Date.now();
			clearInterval(this.reloj);
			this.reloj = setInterval(() => { this.ahora = Date.now(); }, 30000);
			// Si falló la primera carga, se reintenta al abrir.
			if (!this.cargado || this.error) this.cargar();
			this.$nextTick(() => {
				const primero = this.$refs.pop && this.$refs.pop.querySelector('.nt-item, .nt-tab');
				if (primero) primero.focus();
			});
		},
		cerrar(devolverFoco = false) {
			if (!this.abierto) return;
			this.abierto = false;
			clearInterval(this.reloj);
			if (devolverFoco && this.$refs.btn) this.$refs.btn.focus();
		},
		cargar(agregar = false) {
			this.$store.dispatch('notificaciones/cargar', { agregar });
		},
		leerTodas() {
			this.$store.dispatch('notificaciones/leerTodas');
		},
		abrirNotificacion(n) {
			this.cerrar();
			abrir(n);
		},
		alternarSonido() {
			const silenciar = !this.silenciado;
			this.$store.commit('notificaciones/setSilenciado', silenciar);
			// Al activarlo suena una vez, así se sabe cómo es.
			if (!silenciar) sonar();
		},
		async alternarEscritorio() {
			if (this.escritorioOn) {
				this.$store.commit('notificaciones/setEscritorio', false);
				return;
			}
			if (this.permiso !== 'granted') this.permiso = await pedirPermisoEscritorio();
			if (this.permiso === 'granted') this.$store.commit('notificaciones/setEscritorio', true);
		},
		alClicFuera() {
			this.cerrar();
		}
	},
	mounted() {
		document.addEventListener('click', this.alClicFuera);
	},
	beforeDestroy() {
		document.documentElement.classList.remove('nt-panel-abierto');
		document.removeEventListener('click', this.alClicFuera);
		clearInterval(this.reloj);
		clearTimeout(this.timerRing);
	}
};
</script>

<style lang="scss" scoped>
.nt { position: relative; }
.nt :where(h2, p, ul, li) { margin: 0; padding: 0; list-style: none; }
.nt button { font: inherit; }
.nt button:focus { outline: none; }
.nt button:focus-visible { outline: 2px solid var(--ah-orange, #ff7a00); outline-offset: 2px; }
.nt-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

/* Botón */
.nt-btn {
	position: relative; display: grid; place-items: center;
	width: 42px; height: 42px; padding: 0; margin: 0;
	border: 1.5px solid var(--ah-line-2, #ddd2c7); border-radius: 50%; background: #fff;
	color: var(--ah-ink, #1d1a17); cursor: pointer;
	transition: border-color .2s, box-shadow .2s;
	&:hover { border-color: #cbbcae; }
	&[aria-expanded="true"] { border-color: var(--ah-orange, #ff7a00); box-shadow: 0 0 0 4px rgba(255, 122, 0, .16); }
}
.nt-bell { display: grid; transform-origin: 50% 10%; }
.nt-bell ::v-deep svg { width: 20px; height: 20px; }
.nt-btn.is-ring .nt-bell { animation: nt-ring .9s cubic-bezier(.36, .07, .19, .97); }
.nt-count {
	position: absolute; top: -5px; right: -6px;
	min-width: 20px; height: 20px; padding: 0 5px; border-radius: 999px;
	display: grid; place-items: center;
	background: linear-gradient(90deg, #ff8414, #f26b05); color: #fff;
	font-size: 11px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums;
	box-shadow: 0 0 0 2px #fff;
}

/* Panel: mismo estilo suave que los carteles y los formularios */
.nt-pop {
	position: absolute; right: -54px; top: calc(100% + 12px); z-index: 60;
	display: flex; flex-direction: column;
	width: 400px; max-height: min(640px, calc(100vh - 100px));
	border-radius: 22px;
	background: rgba(255, 255, 255, .94);
	-webkit-backdrop-filter: blur(20px) saturate(1.4); backdrop-filter: blur(20px) saturate(1.4);
	border: 1px solid rgba(29, 26, 23, .06);
	box-shadow: 0 1px 2px rgba(29, 26, 23, .04), 0 18px 40px -18px rgba(122, 57, 0, .3), 0 40px 80px -40px rgba(29, 26, 23, .3);
	transform-origin: top right;
	animation: nt-pop .3s cubic-bezier(.22, 1.2, .36, 1) both;
	overflow: hidden;
	color: var(--ah-ink, #1d1a17);
}
.nt-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 4px 10px; padding: 18px 18px 10px 20px; }
.nt-title { font-size: 18px; font-weight: 700; letter-spacing: -.02em; }
.nt-link {
	display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px; margin: -6px -4px -6px auto;
	white-space: nowrap;
	border: 0; border-radius: 999px; background: #fff3e8;
	font-size: 12.5px; font-weight: 700; color: var(--ah-orange-dark, #e8640a); cursor: pointer;
	transition: background .2s;
	&:hover { background: #ffe8d4; }
	::v-deep svg { width: 14px; height: 14px; }
}
/* Filtro como el selector segmentado de los formularios */
.nt-tabs { display: flex; gap: 4px; margin: 2px 18px 10px; padding: 4px; border-radius: 999px; background: #f4efea; align-self: flex-start; }
.nt-tab {
	display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 14px;
	border: 0; border-radius: 999px; background: transparent;
	font-size: 13px; font-weight: 600; color: var(--ah-muted, #6f6760); cursor: pointer;
	transition: background .2s, color .2s, box-shadow .2s;
	&:hover { color: var(--ah-ink, #1d1a17); }
	&.is-on { background: #fff; color: var(--ah-ink, #1d1a17); box-shadow: 0 1px 2px rgba(29, 26, 23, .06), 0 4px 12px -6px rgba(29, 26, 23, .25); }
}
.nt-tab-n { min-width: 18px; padding: 0 5px; border-radius: 999px; background: linear-gradient(180deg, #ff8414, #f26b05); color: #fff; font-size: 11px; line-height: 18px; text-align: center; }

.nt-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 4px 8px 8px; border-top: 1px solid rgba(29, 26, 23, .06); }
.nt-list { display: flex; flex-direction: column; gap: 2px; padding-top: 4px !important; }
.nt-item {
	position: relative; display: flex; align-items: flex-start; gap: 13px; width: 100%;
	padding: 12px 32px 12px 10px; margin: 0; border: 0; border-radius: 16px; background: none;
	text-align: left; color: inherit; cursor: pointer;
	transition: background .2s, transform .15s;
	&:hover, &:focus-visible { background: #faf6f2; }
	&:active { transform: scale(.99); }
	&:focus-visible { outline: none !important; box-shadow: 0 0 0 3px rgba(255, 122, 0, .22); }
	&.is-unread { background: linear-gradient(90deg, #fff6ee, rgba(255, 246, 238, .35)); }
	&.is-unread:hover { background: #fff0e3; }
}
.nt-ico {
	--tint: #f2eee9; --ink-ico: #5d554e;
	display: grid; place-items: center; flex: none; width: 40px; height: 40px; border-radius: 14px;
	background: linear-gradient(145deg, #fff, var(--tint));
	box-shadow: inset 0 0 0 1px rgba(29, 26, 23, .05), 0 6px 14px -8px rgba(29, 26, 23, .25);
	color: var(--ink-ico);
	::v-deep svg { width: 19px; height: 19px; }
}
.nt-item--msg .nt-ico  { --tint: #e8f0ff; --ink-ico: #2b5fb4; }
.nt-item--cal .nt-ico  { --tint: #fff1e4; --ink-ico: #c45a06; }
.nt-item--ok .nt-ico   { --tint: #e6f7ee; --ink-ico: #1c7c45; }
.nt-item--x .nt-ico    { --tint: #fdebe8; --ink-ico: #c0392b; }
.nt-item--pay .nt-ico  { --tint: #efeaff; --ink-ico: #6d3ce0; }
.nt-item--home .nt-ico { --tint: #fff0e3; --ink-ico: #d4610a; }
.nt-txt { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.nt-item-title { font-size: 14px; font-weight: 600; line-height: 1.35; color: var(--ah-ink, #1d1a17); }
.nt-item.is-unread .nt-item-title { font-weight: 700; }
.nt-item-body {
	font-size: 13px; line-height: 1.45; color: #5d554e;
	display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere;
}
.nt-item-meta { font-size: 12px; color: #9a918a; }
.nt-dot { position: absolute; top: 18px; right: 14px; width: 8px; height: 8px; border-radius: 50%; background: #ff7a00; box-shadow: 0 0 0 4px rgba(255, 122, 0, .15); }

.nt-skel { display: flex; flex-direction: column; gap: 8px; padding: 14px 12px 14px 62px; position: relative; }
.nt-skel::before { content: ''; position: absolute; left: 10px; top: 12px; width: 40px; height: 40px; border-radius: 14px; background: #f3eee9; }
.nt-skel span { height: 10px; border-radius: 6px; background: linear-gradient(90deg, #f3eee9, #faf7f4, #f3eee9); background-size: 200% 100%; animation: nt-shine 1.2s linear infinite; }
.nt-skel span:last-child { width: 60%; }

.nt-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 36px 22px 32px; text-align: center; }
.nt-empty-ico { display: grid; place-items: center; width: 56px; height: 56px; margin-bottom: 8px; border-radius: 18px; background: linear-gradient(145deg, #fff, #fff1e4); box-shadow: inset 0 0 0 1px rgba(29, 26, 23, .05), 0 10px 22px -12px rgba(122, 57, 0, .35); color: #e8640a; }
.nt-empty-ico ::v-deep svg { width: 24px; height: 24px; }
.nt-empty-title { font-size: 15px; font-weight: 700; }
.nt-empty-text { font-size: 13px; color: var(--ah-muted, #6f6760); max-width: 260px; }
.nt-btn-sec, .nt-more {
	min-height: 36px; padding: 0 16px; margin-top: 8px;
	border: 1.5px solid var(--ah-line-2, #ddd2c7); border-radius: 999px; background: #fff;
	font-size: 13px; font-weight: 600; color: var(--ah-ink, #1d1a17); cursor: pointer;
	transition: border-color .2s, box-shadow .2s;
	&:hover { border-color: #cbbcae; box-shadow: 0 4px 12px -6px rgba(29, 26, 23, .25); }
	&:disabled { opacity: .6; cursor: default; }
}
.nt-more { display: block; margin: 6px auto 8px; }

.nt-foot { flex: none; display: flex; flex-direction: column; gap: 2px; padding: 8px 12px 12px; border-top: 1px solid rgba(29, 26, 23, .06); background: rgba(250, 246, 242, .8); }
.nt-pref { display: flex; align-items: center; gap: 10px; padding: 6px; }
.nt-pref-ico { display: grid; place-items: center; flex: none; width: 30px; height: 30px; color: var(--ah-ink-2, #3b3530); }
.nt-pref-ico ::v-deep svg { width: 18px; height: 18px; }
.nt-pref-txt { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.nt-pref-title { font-size: 13.5px; font-weight: 600; }
.nt-pref-hint { font-size: 12px; line-height: 1.35; color: var(--ah-muted, #6f6760); }
.nt-switch {
	position: relative; flex: none; width: 42px; height: 24px; padding: 0; margin: 0;
	border: 0; border-radius: 999px; background: #d8cfc6; cursor: pointer; transition: background .2s;
	span { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, .25); transition: transform .2s cubic-bezier(.22, 1, .36, 1); }
	&[aria-checked="true"] { background: linear-gradient(90deg, #ff8414, #f26b05); }
	&[aria-checked="true"] span { transform: translateX(18px); }
	&:disabled { opacity: .5; cursor: not-allowed; }
}

@keyframes nt-pop { from { opacity: 0; transform: translateY(-8px) scale(.96); filter: blur(3px); } to { opacity: 1; transform: none; filter: none; } }
@keyframes nt-ring {
	0%, 100% { transform: rotate(0); }
	15% { transform: rotate(16deg); } 30% { transform: rotate(-14deg); }
	45% { transform: rotate(10deg); } 60% { transform: rotate(-6deg); } 75% { transform: rotate(3deg); }
}
@keyframes nt-shine { to { background-position: -200% 0; } }

@media (max-width: 760px) {
	.nt-btn { width: 40px; height: 40px; }
	.nt-pop { position: fixed; left: 10px; right: 10px; top: 68px; width: auto; max-height: calc(100vh - 84px); max-height: calc(100dvh - 84px); }
}
@media (prefers-reduced-motion: reduce) {
	.nt-pop, .nt-btn.is-ring .nt-bell, .nt-skel span { animation: none; }
}
</style>
